import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../platform/store/platform-slice";
import { useAppStore } from "../../../providers/AppStore";
import {
  dashboardDataType,
  nutritionPlanType,
  workoutPlanType,
} from "../../../providers/dataPlatformReportTypes";
import {
  AugmentedPlatformData,
  AugmentedSystemGroup,
  BioAgeMeasurement,
} from "../data/consultationTypes";
import {
  ConsultationPrep,
  fetchConsultationPrep,
} from "../data/fetchConsultationPrep";
import { hasZoneData } from "../data/energyTrainingZones";
import {
  ServicesPage,
  SchedulingPage,
} from "../../new-reports/types/newReportTypes";

export type ConsultationData = {
  isLoading: boolean;
  hasAnyTest: boolean;
  hasRMR: boolean;
  hasAMR: boolean;
  hasInBody: boolean;
  /** True when the consultation-prep payload includes at least one energy
   *  training zone — drives whether the Energy & Training sub-step appears
   *  in the stepper and sidebar. */
  hasEnergyTraining: boolean;
  /** Current bio-age. Sourced from `prep.bioAge` once the consultation-prep
   *  fetch resolves; before that it's a stable placeholder so the stepper
   *  bar (which renders outside the loading gate) doesn't crash. */
  bioAge: BioAgeMeasurement;
  systems: AugmentedSystemGroup[];
  dashboard: dashboardDataType | null;
  nutritionPlan: nutritionPlanType | null;
  workoutPlan: workoutPlanType | null;
  prescriptionPlan: (ServicesPage | SchedulingPage)[];
  /** The full augmented dataPlatformServer object — handy for debugging. */
  platform: AugmentedPlatformData;
  /** Payload from the separate consultation-prep endpoint (bio-age
   *  projection/history, body composition, energy training, test history,
   *  metric tile values). `null` while the fetch is in flight. */
  prep: ConsultationPrep | null;
};

const safeNumber = (v: any): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Composes selectors over the consultation's data sources. Reads
 * `dataPlatformServer` from Redux, runs the temporary mock-fallback layer
 * to fill backend gaps, and returns a flat shape for the Section components.
 *
 * Triggers `getDataFromPlatform(customerId, role)` on mount if the slice is
 * empty (same dispatch path as `/client/score`).
 */
const useConsultationData = (
  customerId: string | null,
  role: number,
  demoMode: boolean = false
): ConsultationData => {
  // @ts-ignore — AppStoreContext is loosely typed via createContext({})
  const {
    ASGetDataFromPlatform,
    ASGetDataForBiometricTab,
    ASisLoadingPlatformData,
  } = useAppStore();

  const rawPlatform = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer
  );

  // Public demo mode: instead of the two network fetches we serve a captured
  // snapshot of the demo client. The fixture JSON is multi-MB, so we load it
  // via dynamic import (a lazy chunk) only when `demoMode` is active — it never
  // enters the main bundle. `demoData` stays null until the chunk resolves,
  // which keeps `isLoading` true (and the loader up) for that brief moment.
  const [demoData, setDemoData] = useState<{
    platform: AugmentedPlatformData;
    prep: ConsultationPrep;
  } | null>(null);
  useEffect(() => {
    if (!demoMode) return;
    let cancelled = false;
    import("../data/demo/demoData").then((m) => {
      if (!cancelled) {
        setDemoData({ platform: m.demoPlatformData, prep: m.demoConsultationPrep });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [demoMode]);

  // Cast the raw platform payload to the augmented shape (adds the 4 extra
  // fields the consultation reads off `systems[]`). Defaults to a minimal
  // empty stub so callers can render before the platform endpoint resolves.
  // In demo mode the source is the fixture snapshot instead of Redux.
  const platform = useMemo(
    () =>
      (demoMode
        ? demoData?.platform ?? { prescriptionPlan: [] }
        : rawPlatform ?? { prescriptionPlan: [] }) as unknown as AugmentedPlatformData,
    [demoMode, demoData, rawPlatform]
  );

  const score = platform.score;
  const dashboard = platform.dashboard ?? null;
  const systems = score?.systems ?? [];
  const nutritionPlan = platform.programming?.nutritionPlan ?? null;
  const workoutPlan = platform.programming?.workoutPlan ?? null;
  const prescriptionPlan = (platform.prescriptionPlan ?? []) as (
    | ServicesPage
    | SchedulingPage
  )[];

  // Fetch platform data (+ biometric answers) every time the customer or
  // role changes. We don't try to reuse whatever's already in Redux — the
  // slice is single-customer and doesn't track ownership, so reusing it
  // when viewing a different client in the same tab would show stale data.
  // The URL `id` is the source of truth for which customer we're loading;
  // every URL change triggers a fresh fetch.
  //
  // We wait for `role > 0` before firing. The initial `role` value from
  // `AuthUser?.role ?? 0` is `0` until the auth provider's startup
  // `setTimeout(1s)` resolves. Without this guard the effect runs twice
  // per consultation entry — once at role=0, once at role=2 — and we'd
  // see two GetReportv2 + two GetConsultation calls in the Network tab.
  //
  // (`ASGetDataForBiometricTab` hydrates gender/height/age into the
  // biometric answers slice. Since `useClientGender` the sexual-wellness
  // filter reads the report's own gender first, so this is now only the
  // fallback for that — but height/age still come from here.)
  useEffect(() => {
    if (demoMode) return; // demo serves a snapshot — no network fetch
    if (!customerId || role <= 0) return;
    (async () => {
      if (ASGetDataFromPlatform) {
        await ASGetDataFromPlatform(customerId, role);
      }
      if (ASGetDataForBiometricTab) {
        // Sets the customer token via NUTBaseApiAffiliateRequest. Safe to
        // call after `getDataFromPlatform` (which establishes the
        // affiliate→customer scope).
        await ASGetDataForBiometricTab(role);
      }
    })();
    // Intentionally only on mount + when customerId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, role]);

  // Consultation-prep fetch — hits the real `GetConsultation` endpoint and
  // runs the response through the shape adapters in `fetchConsultationPrep`.
  // Fires in parallel with the platform fetch (both need auth, both depend
  // on `customerId`), so the two API calls overlap rather than serialize.
  const [fetchedPrep, setFetchedPrep] = useState<ConsultationPrep | null>(null);
  useEffect(() => {
    if (demoMode) return; // demo serves a snapshot — no network fetch
    if (!customerId || role <= 0) return;
    let cancelled = false;
    fetchConsultationPrep(customerId).then((p) => {
      if (!cancelled) setFetchedPrep(p);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, role, demoMode]);

  // Effective prep: the fixture snapshot in demo mode, otherwise the fetched
  // payload. All downstream derivations + the return read this.
  const prep = demoMode ? demoData?.prep ?? null : fetchedPrep;

  const hasRMR = !!safeNumber(
    nutritionPlan?.nonWorkoutAnalysisChartData?.rmrValue
  );
  const hasAMR = !!safeNumber(
    nutritionPlan?.workoutAnalysisChartData?.workoutValue
  );
  // Body Composition is the synthetic system the backend appends to
  // `score.systems[]`. Only treat it as "present" when it actually carries
  // blood biomarkers — otherwise the detail panel and sidebar row have
  // nothing meaningful to render. Same predicate the score-screen sidebar
  // uses to filter the Body Composition card.
  const hasInBody = systems.some(
    (s) =>
      s?.name?.toLowerCase().includes("body comp") &&
      ((s as any)?.bloodBiomarkers?.length ?? 0) > 0
  );

  // Energy & Training needs a measured zone — the same rule as the Health
  // overview's Energy & Training Zones system. Without one the panel can only
  // render the "No training-zone data" empty state, so the sub-step + sidebar
  // row are hidden entirely.
  const hasEnergyTraining = hasZoneData(prep?.energyTraining?.zones);

  const hasAnyTest = hasRMR || hasAMR || hasInBody;

  // Block the consultation render until BOTH the platform load and the
  // consultation-prep fetch resolve. Keeps every component below free of
  // null-guards on `data.prep`. In demo mode the only gate is the lazy fixture
  // chunk resolving (`demoData`).
  const isLoading = demoMode
    ? demoData === null
    : !!ASisLoadingPlatformData || prep === null;

  // Stable placeholder for `data.bioAge` while the prep fetch is in flight.
  // The defaults here are arbitrary but plausible — once `prep` resolves the
  // real `GetConsultation` values replace them.
  const bioAgeFallback: BioAgeMeasurement = {
    biological: 28,
    chronological: 33,
    reportDate: new Date().toISOString().slice(0, 10),
  };

  return {
    isLoading,
    hasAnyTest,
    hasRMR,
    hasAMR,
    hasInBody,
    hasEnergyTraining,
    bioAge: prep?.bioAge ?? bioAgeFallback,
    systems,
    dashboard,
    nutritionPlan,
    workoutPlan,
    prescriptionPlan,
    platform,
    prep,
  };
};

export default useConsultationData;
